/**
 * Environment Token Configuration
 * Reads token values directly from .env file using import.meta.env
 * This is the SINGLE SOURCE OF TRUTH for all token values
 */

// Read token balance values from .env
export const ENV_TOKEN_BALANCES = {
  GUEST: Number(import.meta.env.VITE_GUEST_TOKEN_BALANCE) || 250,
  REGISTERED: Number(import.meta.env.VITE_REGISTERED_TOKEN_BALANCE) || 550,
  FREE: Number(import.meta.env.VITE_FREE_TOKEN_LIMIT) || 250,
  BASIC: Number(import.meta.env.VITE_BASIC_TOKEN_LIMIT) || 1400, // Increased from 700 to support 3+ complete job applications
  PROFESSIONAL: Number(import.meta.env.VITE_PROFESSIONAL_TOKEN_LIMIT) || 2000,
  ENTERPRISE: Number(import.meta.env.VITE_ENTERPRISE_TOKEN_LIMIT) || 100_000,
} as const;

// Read token costs from .env
// Using type assertions for optional env variables that may not be in type definitions
const env = import.meta.env as Record<string, string | undefined>;

// Unified AI Summary cost (used by both AI_SUMMARY and SUMMARY_GENERATION)
const AI_SUMMARY_COST = Number(env.VITE_AI_SUMMARY_COST) || 30;

export const ENV_TOKEN_COSTS = {
  RESUME_GENERATION: Number(env.VITE_RESUME_GENERATION_COST) || 100,
  COVER_LETTER: Number(env.VITE_COVER_LETTER_COST) || 100,
  ADVANCED_RESUME_ANALYSIS: Number(env.VITE_ADVANCED_RESUME_ANALYSIS_COST) || 150,
  ANALYTICS: Number(env.VITE_ANALYTICS_COST) || 100,
  JOB_FIT_SCORE: Number(env.VITE_JOB_FIT_SCORE_COST) || 100,
  BOOSTER: Number(env.VITE_BOOSTER_COST) || 200,
  LINKEDIN_REWRITE: Number(env.VITE_LINKEDIN_REWRITE_COST) || 200,
  TRAJECTORY: Number(env.VITE_TRAJECTORY_COST) || 100,
  LOCALIZATION: Number(env.VITE_LOCALIZATION_COST) || 100,
  PDF_EXPORT: Number(env.VITE_PDF_EXPORT_COST) || 25,
  WORD_EXPORT: Number(env.VITE_WORD_EXPORT_COST) || 40,
  // AI Writing Assistant - Unified summary cost
  AI_SUMMARY: AI_SUMMARY_COST,
  SUMMARY_GENERATION: AI_SUMMARY_COST, // Alias for backward compatibility
  IMPROVE_WRITING: Number(env.VITE_IMPROVE_WRITING_COST) || 10,
  FIX_SPELLING_GRAMMAR: Number(env.VITE_FIX_SPELLING_GRAMMAR_COST) || 5,
  CHANGE_TONE: Number(env.VITE_CHANGE_TONE_COST) || 10,
  AI_URL_JOB_DETAILS: Number(env.VITE_AI_URL_JOB_DETAILS_COST) || 30,
  // AI Gateway operations
  MARKET_SCOUT: Number(env.VITE_MARKET_SCOUT_COST) || 100,
  EXTRACT_PDF_RESUME: Number(env.VITE_EXTRACT_PDF_RESUME_COST) || 100,
  GENERATE_RESUME: Number(env.VITE_GENERATE_RESUME_COST) || 100,
  INTERVIEW_QUESTION: Number(env.VITE_INTERVIEW_QUESTION_COST) || 50,
  ATS_SCORE: Number(env.VITE_ATS_SCORE_COST) || 100,
} as const;


// Token limits by subscription tier (mapped from balances)
export const ENV_TOKEN_LIMITS = {
  FREE: ENV_TOKEN_BALANCES.FREE,
  BASIC: ENV_TOKEN_BALANCES.BASIC,
  PROFESSIONAL: ENV_TOKEN_BALANCES.PROFESSIONAL,
  ENTERPRISE: ENV_TOKEN_BALANCES.ENTERPRISE,
} as const;

// Export default values for backward compatibility
export const DEFAULT_GUEST_TOKEN_BALANCE = ENV_TOKEN_BALANCES.GUEST;
export const DEFAULT_REGISTERED_TOKEN_BALANCE = ENV_TOKEN_BALANCES.REGISTERED;

/**
 * Debug function to log current environment values
 * Use this to verify your .env values are being read correctly
 */
export const debugTokenConfig = () => {
  // eslint-disable-next-line no-console
  console.log("🔧 Token Configuration from .env:", {
    balances: ENV_TOKEN_BALANCES,
    costs: ENV_TOKEN_COSTS,
    limits: ENV_TOKEN_LIMITS,
  });
};
